import { useEffect, useState } from 'react';
import { getLookupPage, readField } from '../api';

const EMPTY_ROWS = [];
const useLookupPage = (entity, columns) => {
    const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 25 });
    const [sorting, setSorting] = useState([]);
    const [search, setSearch] = useState('');
    const [attempt, setAttempt] = useState(0);
    const [result, setResult] = useState({ rows: EMPTY_ROWS, total: 0, loading: true, error: null });
    const sort = sorting[0];
    const sortBy = sort ? columns.find(column => column.accessorKey === sort.id)?.meta?.sortBy : 'Id';
    const sortDirection = sort?.desc ? 'desc' : 'asc';

    useEffect(() => {
        const controller = new AbortController();
        let current = true;
        setResult({ rows: EMPTY_ROWS, total: 0, loading: true, error: null });
        getLookupPage(entity, { ...pagination, search, sortBy, sortDirection }, controller.signal)
            .then(({ items, total }) => {
                if (!current) return;
                const lastPage = Math.max(0, Math.ceil(total / pagination.pageSize) - 1);
                if (pagination.pageIndex > lastPage) {
                    setPagination(previous => ({ ...previous, pageIndex: lastPage }));
                    return;
                }
                const rows = items.map(item => ({
                    // Retain the record key for backend operations without rendering a column.
                    id: readField(item, 'id'),
                    ...Object.fromEntries(columns.flatMap(({ accessorKey, meta }) =>
                        [accessorKey, ...(meta?.relatedFields || [])].map(field => [field, readField(item, field)]))),
                }));
                setResult({ rows, total, loading: false, error: null });
            })
            .catch(error => {
                if (current) setResult({ rows: EMPTY_ROWS, total: 0, loading: false, error: error.message });
            });
        return () => { current = false; controller.abort(); };
    }, [entity, columns, pagination, search, sortBy, sortDirection, attempt]);

    return {
        ...result, pagination, sorting,
        onPaginationChange: update => setPagination(previous => {
            const next = typeof update === 'function' ? update(previous) : update;
            return { ...next, pageIndex: next.pageSize !== previous.pageSize ? 0 : next.pageIndex };
        }),
        onSortingChange: update => { setSorting(update); setPagination(previous => ({ ...previous, pageIndex: 0 })); },
        onSearch: value => { setSearch(value); setPagination(previous => ({ ...previous, pageIndex: 0 })); },
        retry: () => setAttempt(previous => previous + 1),
        search,
    };
};
export default useLookupPage;
